// NJORDs EGNE ikoner — de kystspesifikke artene, pluss fartøysymbolet.
//
// Skallet (`grep`, `dokk`, `popout`, `kryss`), de universelle tilstandene
// (`info`, `varsel`, `sjekk`, `last`, `nede`) og de begrepene enhver sensorflate
// har (`klokke`, `peiling`, `maal`, `kart`, `søk`, `kamera`) ligger i
// `@skade/sensorflate`. Grensa går ved spørsmålet: BESKRIVER ikonet en sensorART
// eller et domeneobjekt? Da er det NJORDs.
//
// Her er derfor bare de fem som ikke betyr noe utenfor kysten:
//   * `radar`, `rf`, `hydrofon` — tre av postens fire sensorfamilier. (Den
//     fjerde, `kamera`, er felles: både en kystpost og en jegertropp har PTZ.)
//   * `kontakt` — et FARTØYsymbol. En jegertropp sporer ikke båter.
//   * `spektrogram` — hører til RF-/hydrofonstripa i bunnpanelet.
//
// Ikonene bygges med byggeklossene fra pakken (`ikonSvg`, `bane`, `sirkel`), slik
// at strekbredde, hjørner og viewBox er identiske med resten av systemet. Reglene
// fra UX_DESIGNSYSTEM §6 gjelder uendret: ALDRI emoji, aldri Unicode-glyfer
// blandet med SVG, og ingen `innerHTML` — nodene bygges med `createElementNS`.
import { bane, ikonSvg, sirkel, ikon as flateIkon, type Ikonnavn as FlateIkonnavn } from '@skade/sensorflate';

export const NJORD_IKONER = {
  /** Radar: antenne med stråle og to ekkobuer. */
  radar: () => ikonSvg(sirkel(10, 10, 7), bane('M10 10L15 5'), bane('M10 3v1.5'), bane('M13.5 6.5a5 5 0 0 1 0 7')),
  /** RF: sender med to bølger ut til hver side (KrakenRF/DOA). */
  rf: () => ikonSvg(bane('M10 8v9'), sirkel(10, 6, 1.6), bane('M6 9.5a5 5 0 0 1 0-5'), bane('M14 4.5a5 5 0 0 1 0 5'),
    bane('M3.5 11.5a8.5 8.5 0 0 1 0-9'), bane('M16.5 2.5a8.5 8.5 0 0 1 0 9')),
  /** Hydrofon: sensor under en vannlinje, med lydringer. */
  hydrofon: () => ikonSvg(bane('M2 5.5h16'), bane('M10 5.5v6'), sirkel(10, 13.5, 2.4),
    bane('M5.5 13.5a4.5 4.5 0 0 1 1.4-3.2'), bane('M14.5 13.5a4.5 4.5 0 0 0-1.4-3.2')),
  /** Kontakt/target: fartøysymbol i en sirkel (ikke en farget prikk). */
  kontakt: () => ikonSvg(sirkel(10, 10, 7.2), bane('M6.5 11h7l-1.5 2.5h-4z'), bane('M10 6.5v4')),
  spektrogram: () => ikonSvg(bane('M3 16V9'), bane('M6.5 16V5'), bane('M10 16v-9'), bane('M13.5 16V4'), bane('M17 16v-6')),
} as const;

export type NjordIkonnavn = keyof typeof NJORD_IKONER;

/** Alle ikoner NJORD kan be om: flatens felles + postens egne. */
export type Ikonnavn = FlateIkonnavn | NjordIkonnavn;

/**
 * Hent et ikon. Alltid en ny node — SVG-er kan ikke deles mellom to steder.
 *
 * Ett oppslag for begge sett, slik at kallstedene ikke trenger vite HVOR ikonet
 * er definert. Flytter et ikon seg mellom NJORD og pakken (f.eks. hvis ULL også
 * får hydrofon), endres bare denne fila.
 */
export function ikon(navn: Ikonnavn): SVGSVGElement {
  const eget = (NJORD_IKONER as Record<string, (() => SVGSVGElement) | undefined>)[navn];
  return eget ? eget() : flateIkon(navn as FlateIkonnavn);
}
